import { SingleColumnLayout } from '@components'
import { PermissionBoundary, PermissionSet, useBusinessContext, usePermission } from '@context'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { CapitalCard } from './components/CapitalCard'
import { CashCard } from './components/CashCard/CashCard'
import { DeadlinesCard } from './components/DeadlinesCard'
import { FinancialYearCard } from './components/FinancialYearCard'
import { InvoicesCard } from './components/InvoicesCard'
import { KeyFigureCard } from './components/KeyFigureCard'
import { NeedsYouCard } from './components/NeedsYou/NeedsYouCard'
import { PeriodProgressCard } from './components/PeriodProgressCard'
import { QuickActions } from './components/QuickActions'
import { DASHBOARD_PAIR_MIN, useDashboardWidth, WHEN_STACKED, WHEN_WIDE } from './layout'
import { useDashboardInView } from './hooks/useDashboardInView'
import { useKeyFigures } from './hooks/useKeyFigures'

const NoBookkeepingAccessNotice: React.FC = () => {
  const [t] = useTranslation()
  return (
    <NoticeWrapper>
      <h4>{t('dashboard.noBookkeepingAccess.title')}</h4>
      <p>{t('dashboard.noBookkeepingAccess.description')}</p>
    </NoticeWrapper>
  )
}

const WelcomeHeader: React.FC = () => {
  const [t] = useTranslation()

  return (
    <HeaderBlock>
      <HeaderTitle>{t('dashboard.welcome')}</HeaderTitle>
    </HeaderBlock>
  )
}

const KeyFigures: React.FC = () => {
  const { ref, isInView } = useDashboardInView()
  const { figures, isLoading } = useKeyFigures({ enabled: isInView })

  return (
    <FigureRow ref={ref}>
      {figures.map(figure => (
        <KeyFigureCard key={figure.key} figure={figure} isLoading={isLoading} />
      ))}
    </FigureRow>
  )
}

export const DashboardPage: React.FC = () => {
  const { slug } = useBusinessContext()
  const { ref: contentRef, isStacked, isNarrow, isWide } = useDashboardWidth()
  const canViewBookkeeping = usePermission(PermissionSet.BOOKKEEPING_VIEW)

  return (
    <SingleColumnLayout header={<WelcomeHeader />}>
      <Page>
        <Content
          ref={contentRef}
          data-dashboard-stacked={String(isStacked)}
          data-dashboard-narrow={String(isNarrow)}
          data-dashboard-wide={String(isWide)}
        >
          <PermissionBoundary
            required={PermissionSet.DATE_RANGE_VIEW}
            alternativeContent={<NoBookkeepingAccessNotice />}
          >
            {canViewBookkeeping ? (
              <>
                <Hero>
                  <CashSlot>
                    <PermissionBoundary required={PermissionSet.ACCOUNTS_VIEW}>
                      <CashCard />
                    </PermissionBoundary>
                  </CashSlot>
                  <AttentionPair>
                    <NeedsYouCard />
                    <DeadlinesCard />
                  </AttentionPair>
                  <QaSlot>
                    <QuickActions />
                  </QaSlot>
                </Hero>
                <KeyFigures />
                <Rail>
                  <CapitalCard />
                  <InvoicesCard />
                </Rail>
                <FinancialYearCard key={slug} />
              </>
            ) : (
              <>
                <NeedsYouCard />
                <PermissionBoundary required={PermissionSet.REPORTING_VIEW}>
                  <PeriodProgressCard />
                </PermissionBoundary>
              </>
            )}
          </PermissionBoundary>
        </Content>
      </Page>
    </SingleColumnLayout>
  )
}

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  min-width: 0;
  overflow: hidden;
`

const HeaderTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.menuBg};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    font-size: ${({ theme }) => theme.fontSize.md}rem;
  }
`

const Page = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.neutralGray};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
`

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.md}rem;

  ${WHEN_STACKED} {
    grid-template-columns: minmax(0, 1fr);
  }
`

const CashSlot = styled.div`
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  align-self: stretch;

  > * {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    align-self: stretch;
  }

  ${WHEN_WIDE} {
    grid-row: 1 / 3;
  }
`

const QaSlot = styled.div`
  grid-column: 1 / -1;
  grid-row: 2;
  min-width: 0;

  ${WHEN_WIDE} {
    grid-column: 2;
    grid-row: 2;
  }

  ${WHEN_STACKED} {
    grid-column: 1;
    grid-row: 3;
  }

  &:empty {
    display: none;
  }

  > * {
    width: 100%;
  }
`

const AttentionPair = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  align-content: flex-start;
  gap: ${({ theme }) => theme.spacing.md}rem;
  grid-column: 2;
  grid-row: 1;
  min-width: 0;

  > * {
    flex: 1 0 min(100%, ${DASHBOARD_PAIR_MIN});
    min-width: min(100%, ${DASHBOARD_PAIR_MIN});
  }

  ${WHEN_STACKED} {
    grid-column: 1;
    grid-row: 2;
  }
`

const FigureRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
  min-width: 0;

  > * {
    flex: 1 1 min(100%, 12rem);
    min-width: 0;
  }
`

const Rail = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
  min-width: 0;

  > * {
    flex: 1 0 min(100%, ${DASHBOARD_PAIR_MIN});
    min-width: min(100%, ${DASHBOARD_PAIR_MIN});
  }
`

const NoticeWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.lg}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};

  h4 {
    margin: 0 0 ${({ theme }) => theme.spacing.xxs}rem;
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.menuBg};
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.metalGray};
  }
`
